import { useCallback, useEffect, useRef, useState } from "react";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  ShieldAlert,
  Trash2,
  Upload,
} from "lucide-react";
import { conversationalAI, type ConversationalAIKbDocumentOut } from "../apiv1";
import { useAuth } from "../auth";

interface Props {
  projectId: string;
}

const DOC_TYPES: { value: string; label: string }[] = [
  { value: "source_data_dictionary", label: "Source data dictionary" },
  { value: "target_data_dictionary", label: "Target data dictionary" },
  { value: "source_data_model", label: "Source data model" },
  { value: "target_data_model", label: "Target data model" },
  { value: "source_functional_spec", label: "Source functional spec" },
  { value: "target_functional_spec", label: "Target functional spec" },
  { value: "engagement_rule_pack", label: "Engagement rule pack" },
  { value: "other", label: "Other" },
];

/** Status badge for one KB document (§7.3). `pending_classification_review`
 * gets its own distinct treatment per §4.2 step 4 — visible to everyone,
 * but the clearance action itself is admin-only. */
function StatusBadge({
  status,
}: {
  status: ConversationalAIKbDocumentOut["status"];
}) {
  if (status === "ready")
    return <span className="ztd-badge ztd-badge-success">Ready</span>;
  if (status === "processing")
    return (
      <span className="ztd-badge ztd-badge-info flex items-center gap-1">
        <Loader2 size={11} className="animate-spin" /> Processing
      </span>
    );
  if (status === "pending_classification_review")
    return (
      <span className="ztd-badge ztd-badge-warning flex items-center gap-1">
        <ShieldAlert size={11} /> Needs review
      </span>
    );
  return <span className="ztd-badge ztd-badge-critical">Failed</span>;
}

export default function KnowledgeBasePanel({ projectId }: Props) {
  const { hasPermission } = useAuth();
  const canClearReview = hasPermission("admin:project");

  const [open, setOpen] = useState(false);
  const [documents, setDocuments] = useState<ConversationalAIKbDocumentOut[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [docType, setDocType] = useState("other");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setDocuments(await conversationalAI.listKbDocuments(projectId));
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Failed to load knowledge base documents.",
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const uploadFile = useCallback(
    async (file: File) => {
      setUploading(true);
      setError(null);
      try {
        const doc = await conversationalAI.uploadKbDocument(
          projectId,
          file,
          docType,
        );
        setDocuments((prev) => [doc, ...prev]);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Upload failed.");
      } finally {
        setUploading(false);
      }
    },
    [projectId, docType],
  );

  const handleDelete = useCallback(
    async (documentId: string, filename: string) => {
      // Matches the confirm-before-destructive-action pattern already
      // established elsewhere in this app (e.g. SemanticModelCreatorView's
      // column/entity deletes) — this was previously a single-click delete
      // with no confirmation at all.
      if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) {
        return;
      }
      try {
        await conversationalAI.deleteKbDocument(documentId, projectId);
        setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Delete failed.");
      }
    },
    [projectId],
  );

  const handleClearReview = useCallback(
    async (documentId: string) => {
      try {
        const cleared = await conversationalAI.clearKbDocumentReview(
          documentId,
          projectId,
        );
        setDocuments((prev) =>
          prev.map((d) => (d.id === documentId ? cleared : d)),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : "Clearance failed.");
      }
    },
    [projectId],
  );

  return (
    <div className="glass-card rounded-2xl p-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-white/80">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <BookOpen size={14} className="text-ai" />
          Knowledge base
        </span>
        {documents.length > 0 && (
          <span className="text-[11px] text-white/30">
            {documents.length} document{documents.length === 1 ? "" : "s"}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {error && (
            <div className="p-2.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300">
              {error}
            </div>
          )}

          {/* Upload zone — same drop-zone UX as the profiler's own upload dialog */}
          <div className="flex items-center gap-2">
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="ztd-input flex-shrink-0 w-auto text-xs"
            >
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) uploadFile(file);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") fileInputRef.current?.click();
            }}
            aria-label="Drop a document here or click to browse"
            className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
              dragging
                ? "border-ai/60 bg-ai/5"
                : "border-white/10 hover:border-ai/40"
            }`}
          >
            {uploading ? (
              <Loader2
                size={20}
                className="mx-auto mb-2 animate-spin text-ai"
              />
            ) : (
              <Upload size={20} className="mx-auto mb-2 text-white/30" />
            )}
            <p className="text-xs text-white/50">
              Drop a document here, or click to browse
            </p>
            <p className="text-[11px] text-white/25 mt-1">
              PDF, DOCX, XLSX, XLS, CSV, TXT, MD
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadFile(file);
              e.target.value = "";
            }}
          />

          {/* Document list */}
          {loading ? (
            <p className="text-xs text-white/30 text-center py-4">Loading…</p>
          ) : documents.length === 0 ? (
            <p className="text-xs text-white/30 text-center py-4">
              No documents uploaded yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {documents.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-center justify-between gap-2 bg-surface-200 rounded-xl px-3 py-2.5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText
                      size={14}
                      className="text-white/30 flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-xs text-white/70 truncate">
                        {doc.filename}
                      </p>
                      <p className="text-[10px] text-white/25">
                        {DOC_TYPES.find((t) => t.value === doc.doc_type)
                          ?.label ?? doc.doc_type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusBadge status={doc.status} />
                    {doc.status === "pending_classification_review" &&
                      canClearReview && (
                        <button
                          onClick={() => handleClearReview(doc.id)}
                          className="ztd-btn ztd-btn-sm ztd-btn-secondary"
                          title="This document needs classification review before it can ground answers."
                        >
                          Clear
                        </button>
                      )}
                    <button
                      onClick={() => handleDelete(doc.id, doc.filename)}
                      className="p-1.5 rounded hover:bg-white/10 text-white/30 hover:text-red-400 transition"
                      title="Delete document"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
